/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/**
 * API convention:
 *
 * This component is meant to be embedded in a parent component that has
 * a parent form.  It's expected that the parent passes it's FormGroup
 * reference in as an Input to this component.  Further, the parent must
 * also configure the form control as part of creating the form group,
 * specicifically referring to this component with the name "description",
 * and supplying a required validator.  The parent can optionally supply an
 * initial text value too.
 *
 */

import {
	Component,
	Input,
	Output,
	ChangeDetectionStrategy,
	EventEmitter,
	Self,
	Optional,
	ChangeDetectorRef,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { BehaviorSubject } from 'rxjs';
import { TicketEditService } from '../../ticket-edit.service';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { TicketCounts, CombinedTicketTypes } from 'src/app/app-data/models/EditTicketsCombined';
import {
	TICKET_TYPE_CONSTRAINT_ACCEPTED,
	TICKET_TYPE_CONSTRAINT_ASSIGNED,
	TICKET_TYPE_CONSTRAINT_UNASSIGNED,
	TICKET_TYPE_MILESTONE,
	TICKET_TYPE_TASK,
} from '@touchplan/strings';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService, ILoggerService } from '@touchplan/logging';

const PrettyTypeName = {
	task: TICKET_TYPE_TASK,
	constraint: TICKET_TYPE_CONSTRAINT_UNASSIGNED,
	milestone: TICKET_TYPE_MILESTONE,
	constraint_assigned: TICKET_TYPE_CONSTRAINT_ASSIGNED,
	constraint_accepted: TICKET_TYPE_CONSTRAINT_ACCEPTED,
};

/**
 * Displays a thumbnail of the selected ticket type when multiple
 * tickets are editted.  Provides a menu to switch between types and
 * to update the form data when the type changes.
 *
 */
@Component({
	selector: 'multi-edit-thumbnail',
	templateUrl: './multi-edit-thumbnail.component.html',
	styleUrls: ['./multi-edit-thumbnail.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LoggerServiceFactory.provide('MultiEditThumbnail')],
})
export class MultiEditThumbnail extends TicketEditBaseComponent {
	/**
	 * Input of the TicketCounts object from the EditCombined data.
	 * Component uses this to generate a dropdown of switchable types.
	 */
	@Input() public typeCounts: TicketCounts;
	/**
	 * The current type we have selected.
	 */
	@Input() public currentType: CombinedTicketTypes;
	/**
	 * @Outputs a type changed event to signal the parent that the containers
	 * needs updating to the new type.
	 */
	@Output() public typeChanged: EventEmitter<string> = new EventEmitter<string>();
	/**
	 * BehaviourSubject to track if the dropdown menu is active or not
	 */
	public menuShown$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private _visible = false;
	/**
	 * Blah blah construct
	 * @param {TicketEditService} ticketEditService
	 * @param {NgControl} control
	 * @Param {MultiWrapperComponent} multiWrapper
	 */

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef,
		private ticketEditService: TicketEditService,
		private logger: DefaultLoggerService
	) {
		super(control, multiWrapper, cd);
	}

	/**
	 * Sets the new ticket type. Invoked when a ticket type from the selection dropdown is selected
	 * @param {CombinedTicketTypes} type
	 */
	public setCurrentType(type: CombinedTicketTypes): void {
		this.ticketEditService.switchTypeFromUi(type);
	}
	/**
	 * Expands the ticket type selection menu
	 */
	public toggleMenu() {
		const next = !this._visible;
		this.menuShown$.next(next);
		this._visible = next;
	}

	protected closeMenu(): void {
		this.menuShown$.next(false);
		this._visible = false;
	}

	public getPrettyTypeName(type: string): string {
		this.logger.log('TYPE IS', type, this.typeCounts[type]);
		const isS: string = this.typeCounts[type] > 1 ? 's' : '';
		const prettyName = PrettyTypeName[type] + isS;
		return prettyName;
	}
}
